/**
 * store/index.ts
 * Single Zustand store for the entire app.
 * - MOCK_MODE = true  → runs on seed data, no backend needed
 * - MOCK_MODE = false → all actions call the real API (api/client.ts)
 * Flip MOCK_MODE to false when backend is live — zero other changes needed.
 */

import { create } from 'zustand';
import { notesApi, foldersApi, searchApi, authApi, getToken, setToken, clearAuth } from '../api/client';
import { toast } from '../components/ui/Toast';
import type { Note, Folder, User, SearchResult, SyncStatus, ActiveView } from '../types';

const MOCK_MODE = false;

// ─── SEED DATA ────────────────────────────────────────────────────────────────
const SEED_USER: User = {
  id: 'u1', name: 'Ada Okonkwo', email: 'ada@example.com',
  plan: 'free', emailVerified: true, avatarUrl: null,
  storageUsedMb: 34, storageLimitMb: 200,
  createdAt: new Date().toISOString(),
};

const SEED_FOLDERS: Folder[] = [
  { id:'f1', name:'Work notes',  color:'#F5A623', parentId:null, userId:'u1', noteCount:12, createdAt:'', updatedAt:'' },
  { id:'f2', name:'Formulas',    color:'#C4811A', parentId:null, userId:'u1', noteCount:8,  createdAt:'', updatedAt:'' },
  { id:'f3', name:'Study',       color:'#16A34A', parentId:null, userId:'u1', noteCount:5,  createdAt:'', updatedAt:'' },
  { id:'f4', name:'Personal',    color:'#7C3AED', parentId:null, userId:'u1', noteCount:14, createdAt:'', updatedAt:'' },
  { id:'f5', name:'Projects',    color:'#DB2777', parentId:'f1', userId:'u1', noteCount:3,  createdAt:'', updatedAt:'' },
];

const SEED_NOTES: Note[] = [
  { id:'n1', title:'Q3 Financial Model',  body:'<h2>Q3 Financial Model</h2><p>The <strong>IRR formula</strong> calculates internal rate of return on projected cash flows. Use =IRR(values, guess) in Excel.</p><ul><li>Revenue target: ₦45M</li><li>EBITDA margin: 22%</li><li>IRR threshold: 18%</li></ul>', plainText:'Q3 Financial Model. The IRR formula calculates internal rate of return on projected cash flows.', folderId:'f1', tags:['finance','quarterly'], userId:'u1', isPinned:true, isPublic:false, publicToken:null, sharedAt:null,  createdAt:new Date(Date.now()-86400000*1).toISOString(), updatedAt:new Date(Date.now()-3600000).toISOString() },
  { id:'n2', title:'Excel Reference Sheet',body:'<h2>Excel Shortcuts</h2><ul><li><code>IRR formula</code> — =IRR(cashflows, guess)</li><li><code>NPV</code> — =NPV(rate, values)</li><li><code>VLOOKUP</code> — =VLOOKUP(value, range, col, false)</li></ul>', plainText:'Excel Reference Sheet. IRR formula. NPV. VLOOKUP.', folderId:'f2', tags:['excel','formulas'], userId:'u1', isPinned:true, isPublic:false, publicToken:null, sharedAt:null,  createdAt:new Date(Date.now()-86400000*3).toISOString(), updatedAt:new Date(Date.now()-7200000).toISOString() },
  { id:'n3', title:'MBA Notes — Week 4',   body:'<h2>Finance Week 4</h2><p>Comparing NPV and IRR formula — discount rate sensitivity analysis. When IRR exceeds WACC, project creates value.</p>', plainText:'MBA Notes Week 4. Comparing NPV and IRR formula. When IRR exceeds WACC project creates value.', folderId:'f3', tags:['mba','finance'],  userId:'u1', isPinned:false, isPublic:false, publicToken:null, sharedAt:null, createdAt:new Date(Date.now()-86400000*5).toISOString(), updatedAt:new Date(Date.now()-86400000).toISOString() },
  { id:'n4', title:'Client Meeting — Apex',body:'<h2>Apex Corp Meeting</h2><p>Discussed Q4 deliverables. Client wants dashboard by November 15. Budget approved: ₦8.5M.</p>', plainText:'Client Meeting Apex Corp. Discussed Q4 deliverables. Budget approved 8.5M.', folderId:'f1', tags:['client','meeting'], userId:'u1', isPinned:false, isPublic:false, publicToken:null, sharedAt:null, createdAt:new Date(Date.now()-86400000*2).toISOString(), updatedAt:new Date(Date.now()-43200000).toISOString() },
  { id:'n5', title:'Physics — Mechanics',  body:'<h2>Mechanics Summary</h2><p>Newton\'s second law: F = ma. Kinetic energy: KE = ½mv².</p>', plainText:'Physics Mechanics. Newton second law F=ma. Kinetic energy KE = half mv squared.', folderId:'f3', tags:['physics','study'],  userId:'u1', isPinned:false, isPublic:false, publicToken:null, sharedAt:null, createdAt:new Date(Date.now()-86400000*7).toISOString(), updatedAt:new Date(Date.now()-86400000*2).toISOString() },
  { id:'n6', title:'Goals 2026',           body:'<h2>My Goals for 2026</h2><ol><li>Launch Jötr to 5,000 users</li><li>Complete MBA programme</li><li>Build emergency fund (6 months)</li><li>Read 24 books</li></ol>', plainText:'Goals 2026. Launch Jötr to 5000 users. Complete MBA. Build emergency fund.', folderId:'f4', tags:['goals','personal'], userId:'u1', isPinned:false, isPublic:false, publicToken:null, sharedAt:null, createdAt:new Date(Date.now()-86400000*10).toISOString(), updatedAt:new Date(Date.now()-86400000*3).toISOString() },
];

// ─── STORE SHAPE ──────────────────────────────────────────────────────────────
interface JötrState {
  user: User | null;
  isAuthenticated: boolean;
  authLoading: boolean;
  notes: Note[];
  notesLoading: boolean;
  folders: Folder[];
  foldersLoading: boolean;
  activeView: ActiveView;
  activeFolderId: string | null;
  activeTag: string | null;
  openNoteId: string | null;
  sidebarOpen: boolean;
  searchQuery: string;
  searchResults: SearchResult[];
  searchLoading: boolean;
  syncStatus: SyncStatus;
  lastSavedAt: string | null;

  initAuth:     () => void;
  login:        (email: string, password: string) => Promise<void>;
  signup:       (name: string, email: string, password: string) => Promise<void>;
  logout:       () => void;
  setUser:      (user: User) => void;
  loadNotes:    () => Promise<void>;
  createNote:   (folderId?: string | null) => Promise<Note>;
  updateNote:   (id: string, patch: Partial<Note>) => Promise<void>;
  deleteNote:   (id: string) => Promise<void>;
  pinNote:      (id: string, isPinned: boolean) => Promise<void>;
  loadFolders:  () => Promise<void>;
  createFolder: (name: string, color: string, parentId?: string | null) => Promise<void>;
  updateFolder: (id: string, patch: { name?: string; color?: string }) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  search:       (query: string, folderId?: string | null, tag?: string | null) => Promise<void>;
  clearSearch:  () => void;
  setActiveView:(view: ActiveView, folderId?: string | null, tag?: string | null) => void;
  setOpenNote:  (id: string | null) => void;
  toggleSidebar:() => void;
}

// ─── STORE ────────────────────────────────────────────────────────────────────
export const useStore = create<JötrState>((set, get) => ({
  // In MOCK_MODE, start already authenticated — no loading flash
  user:            MOCK_MODE ? SEED_USER : null,
  isAuthenticated: MOCK_MODE ? true : false,
  authLoading:     MOCK_MODE ? false : true,
  notes: [], notesLoading: false,
  folders: [], foldersLoading: false,
  activeView: 'all', activeFolderId: null, activeTag: null,
  openNoteId: null, sidebarOpen: true,
  searchQuery: '', searchResults: [], searchLoading: false,
  syncStatus: 'idle', lastSavedAt: null,

  // ── Auth ──────────────────────────────────────────────────────────────────
  initAuth: () => {
    if (MOCK_MODE) {
      // Already set in initial state — nothing to do
      return;
    }
    // Real auth: check JWT token
    const token = getToken();
    if (!token) { set({ authLoading: false }); return; }
    authApi.me()
      .then(user => set({ user, isAuthenticated: true, authLoading: false }))
      .catch(() => { clearAuth(); set({ authLoading: false }); });
  },

  login: async (email, password) => {
    if (MOCK_MODE) {
      set({ user: SEED_USER, isAuthenticated: true });
      return;
    }
    const res = await authApi.login({ email, password });
    setToken(res.token);
    set({ user: res.user, isAuthenticated: true });
  },

  signup: async (name, email, password) => {
    if (MOCK_MODE) {
      set({ user: { ...SEED_USER, name, email }, isAuthenticated: true });
      return;
    }
    const res = await authApi.signup({ name, email, password });
    setToken(res.token);
    set({ user: res.user, isAuthenticated: true });
  },

  setUser: (user: User) => set({ user }),

  logout: () => {
    clearAuth();
    set({ user: null, isAuthenticated: false, notes: [], folders: [] });
    window.location.href = '/';
  },

  // ── Notes ─────────────────────────────────────────────────────────────────
  loadNotes: async () => {
    set({ notesLoading: true });
    if (MOCK_MODE) { set({ notes: SEED_NOTES, notesLoading: false }); return; }
    try {
      const notes = await notesApi.list();
      set({ notes: Array.isArray(notes) ? notes : [], notesLoading: false });
    } catch { set({ notesLoading: false }); }
  },

  createNote: async (folderId = null) => {
    const blank: Note = {
      id: `n${Date.now()}`, title: 'Untitled note', body: '', plainText: '',
      folderId, tags: [], userId: get().user?.id || '', isPinned: false, isPublic: false, publicToken: null, sharedAt: null,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    if (MOCK_MODE) {
      set(s => ({ notes: [blank, ...s.notes] }));
      return blank;
    }
    const note = await notesApi.create({ title: blank.title, body: '', plainText: '', folderId });
    set(s => ({ notes: [note, ...s.notes] }));
    return note;
  },

  updateNote: async (id, patch) => {
    set(s => ({
      notes: s.notes.map(n => n.id === id ? { ...n, ...patch, updatedAt: new Date().toISOString() } : n),
      syncStatus: 'saving',
    }));
    if (MOCK_MODE) {
      setTimeout(() => set({ syncStatus: 'saved', lastSavedAt: new Date().toISOString() }), 600);
      return;
    }
    try {
      await notesApi.update(id, patch);
      set({ syncStatus: 'saved', lastSavedAt: new Date().toISOString() });
    } catch { set({ syncStatus: 'error' }); toast.error('Could not save note — check your connection'); }
  },

  deleteNote: async (id) => {
    set(s => ({ notes: s.notes.filter(n => n.id !== id) }));
    toast.success('Note deleted');
    if (MOCK_MODE) return;
    await notesApi.delete(id);
  },

  pinNote: async (id, isPinned) => {
    set(s => ({ notes: s.notes.map(n => n.id === id ? { ...n, isPinned } : n) }));
    toast.success(isPinned ? 'Note pinned!' : 'Note unpinned');
    if (MOCK_MODE) return;
    await notesApi.pin(id, isPinned);
  },

  // ── Folders ───────────────────────────────────────────────────────────────
  loadFolders: async () => {
    set({ foldersLoading: true });
    if (MOCK_MODE) { set({ folders: SEED_FOLDERS, foldersLoading: false }); return; }
    try {
      const folders = await foldersApi.list();
      set({ folders, foldersLoading: false });
    } catch { set({ foldersLoading: false }); }
  },

  createFolder: async (name, color, parentId = null) => {
    const folder: Folder = {
      id: `folder_${Date.now()}`, name, color, parentId,
      userId: get().user?.id || '', noteCount: 0,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
    };
    if (MOCK_MODE) { set(s => ({ folders: [...s.folders, folder] })); toast.success('Folder created!'); return; }
    const created = await foldersApi.create({ name, color, parentId });
    set(s => ({ folders: [...s.folders, created] }));
    toast.success('Folder created!');
  },

  updateFolder: async (id, patch) => {
    set(s => ({ folders: s.folders.map(f => f.id === id ? { ...f, ...patch } : f) }));
    if (MOCK_MODE) return;
    await foldersApi.update(id, patch);
  },

  deleteFolder: async (id) => {
    set(s => ({
      folders: s.folders.filter(f => f.id !== id),
      notes: s.notes.map(n => n.folderId === id ? { ...n, folderId: null } : n),
    }));
    toast.success('Folder deleted');
    if (MOCK_MODE) return;
    await foldersApi.delete(id);
  },

  // ── Search ────────────────────────────────────────────────────────────────
  search: async (query, folderId = null, tag = null) => {
    set({ searchQuery: query, searchLoading: true, activeView: 'search' });
    if (MOCK_MODE) {
      const q = query.toLowerCase();
      const results: SearchResult[] = get().notes
        .filter(n => n.title.toLowerCase().includes(q) || n.plainText.toLowerCase().includes(q))
        .map(n => {
          const idx = n.plainText.toLowerCase().indexOf(q);
          const start = Math.max(0, idx - 40);
          const raw = n.plainText.substring(start, start + 120);
          const snippet = raw.replace(new RegExp(query, 'gi'), m => `<mark>${m}</mark>`);
          return {
            note: n,
            snippet,
            matchCount: (n.plainText.match(new RegExp(query, 'gi')) || []).length,
          };
        });
      set({ searchResults: results, searchLoading: false });
      return;
    }
    try {
      const res = await searchApi.query({ query, folderId, tag });
      set({ searchResults: res.results, searchLoading: false });
    } catch { set({ searchLoading: false }); }
  },

  clearSearch: () => set({ searchQuery: '', searchResults: [], activeView: 'all' }),

  // ── UI ────────────────────────────────────────────────────────────────────
  setActiveView: (view, folderId = null, tag = null) =>
    set({ activeView: view, activeFolderId: folderId, activeTag: tag, openNoteId: null }),

  setOpenNote:    (id) => set({ openNoteId: id }),
  toggleSidebar:  ()  => set(s => ({ sidebarOpen: !s.sidebarOpen })),
}));

// ─── SELECTORS ────────────────────────────────────────────────────────────────
export const selectVisibleNotes = (state: JötrState): Note[] => {
  switch (state.activeView) {
    case 'folder':  return state.notes.filter(n => n.folderId === state.activeFolderId);
    case 'tag':     return state.notes.filter(n => state.activeTag !== null && n.tags.includes(state.activeTag));
    case 'pinned':  return state.notes.filter(n => n.isPinned);
    case 'search':  return state.searchResults.map(r => r.note);
    default:        return state.notes;
  }
};

export const selectAllTags = (state: JötrState): string[] =>
  Array.from(new Set(state.notes.flatMap(n => n.tags))).sort();